<template>
  <v-container class="modern-view pb-12">
    <ValidationObserver v-slot="{ handleSubmit }">
      <v-form @submit.prevent="handleSubmit(saveUser)">
        <!-- Premium Header -->
        <v-row class="mb-8 align-center px-4 pt-4">
          <div class="d-flex align-center">
            <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
              <v-icon color="white">fas fa-user-edit</v-icon>
            </v-avatar>
            <div>
              <div class="text-h4 font-weight-black primary--text lh-1">
                {{ user.id ? 'Actualizar' : 'Crear' }} <span class="secondary--text">Usuario</span>
              </div>
              <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">
                {{ user.id ? 'Modificación de perfil y permisos' : 'Registro de nuevo colaborador' }}
              </div>
            </div>
          </div>
          <v-spacer></v-spacer>
          <div class="d-flex align-center">
            <v-btn @click="$router.push('/users')" text class="grey--text font-weight-bold mr-4 rounded-lg">
              <v-icon left small>fas fa-chevron-left</v-icon> Volver
            </v-btn>
            <v-btn type="submit" color="primary" class="shadow-sm rounded-lg px-6 font-weight-black" :loading="loading">
              <v-icon left small>fas fa-save</v-icon> Guardar Usuario
            </v-btn>
          </div>
        </v-row>

        <v-row>
          <!-- Left Column: Basic Info & Access -->
          <v-col cols="12" md="8">
            <v-card outlined class="modern-card pa-8 mb-6 shadow-sm">
              <div class="d-flex align-center mb-6">
                <v-avatar size="32" color="primary lighten-5" class="mr-3">
                  <v-icon color="primary" small>fas fa-id-card</v-icon>
                </v-avatar>
                <span class="text-h6 font-weight-black primary--text">Información Personal</span>
              </div>
              
              <v-row dense>
                <v-col cols="12" md="6">
                  <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Nombre</div>
                  <ValidationProvider name="Nombre" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model='user.first_name'
                      outlined dense
                      :error-messages="errors"
                      class="modern-input"
                      placeholder="Ej: Juan"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Apellido</div>
                  <ValidationProvider name="Apellido" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model='user.last_name'
                      outlined dense
                      :error-messages="errors"
                      class="modern-input"
                      placeholder="Ej: Pérez"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Correo Electrónico</div>
                  <ValidationProvider name="Email" rules="required|email" v-slot="{ errors }">
                    <v-text-field
                      v-model='user.email'
                      :disabled="!!user.id"
                      outlined dense
                      :error-messages="errors"
                      class="modern-input"
                      placeholder="juan.perez@runflow.com"
                      prepend-inner-icon="fas fa-envelope"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Teléfono</div>
                  <ValidationProvider name="Teléfono" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model='user.phone'
                      outlined dense
                      :error-messages="errors"
                      class="modern-input"
                      placeholder="+56 9 ..."
                      prepend-inner-icon="fas fa-phone"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
              </v-row>

              <v-divider class="my-8"></v-divider>

              <div class="d-flex align-center mb-6">
                <v-avatar size="32" color="secondary lighten-5" class="mr-3">
                  <v-icon color="secondary" small>fas fa-shield-alt</v-icon>
                </v-avatar>
                <span class="text-h6 font-weight-black secondary--text">Seguridad y Acceso</span>
              </div>

              <v-row dense>
                <v-col cols="12" md="6">
                  <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Rol de Usuario</div>
                  <v-select
                    outlined dense
                    :items="roles"
                    item-value="id"
                    item-text="name"
                    v-model="user.role"
                    class="modern-input"
                  >
                    <template v-slot:item="{ item }">
                      <v-avatar size="24" color="secondary lighten-5" class="mr-3">
                        <v-icon color="secondary" x-small>fas fa-user-tag</v-icon>
                      </v-avatar>
                      <span class="body-2 font-weight-bold">{{ item.name }}</span>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <div v-if="!user.id">
                    <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Contraseña Inicial</div>
                    <ValidationProvider name="Contraseña" rules="required|min:4" v-slot="{ errors }">
                      <v-text-field
                        v-model='user.password'
                        type="password"
                        outlined dense
                        :error-messages="errors"
                        class="modern-input"
                        placeholder="••••••••"
                        persistent-hint
                        hint="Min. 4 caracteres"
                      ></v-text-field>
                    </ValidationProvider>
                  </div>
                  <div v-else class="d-flex flex-column justify-end h-100 pb-2">
                    <v-btn 
                      @click="sendEmail" 
                      :loading="loading" 
                      :disabled="sent" 
                      outlined
                      :color="error ? 'error' : 'primary'"
                      class="rounded-lg font-weight-black text-none"
                      block
                    >
                      <v-icon left small>{{ sent ? 'fas fa-check-circle' : 'fas fa-key' }}</v-icon>
                      {{ sent ? 'Correo Instrucciones Enviado' : 'Enviar Reset de Contraseña' }}
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </v-col>

          <!-- Right Column: Settings -->
          <v-col cols="12" md="4">
            <v-card outlined class="modern-card pa-8 mb-6 shadow-sm h-100">
              <div class="d-flex align-center mb-6">
                <v-avatar size="32" color="orange lighten-5" class="mr-3">
                  <v-icon color="orange darken-3" small>fas fa-briefcase</v-icon>
                </v-avatar>
                <span class="text-h6 font-weight-black grey--text text--darken-3">Config. Comercial</span>
              </div>

              <div class="d-flex flex-column" style="gap: 12px;">
                <v-sheet outlined class="pa-4 rounded-lg bg-lightgrey border-dashed d-flex align-center">
                  <v-checkbox v-model="user.is_seller" hide-details class="mt-0 pt-0"></v-checkbox>
                  <div>
                    <div class="body-2 font-weight-black primary--text">Perfil Vendedor</div>
                    <div class="caption grey--text">Habilita gestión de ventas y comisiones</div>
                  </div>
                </v-sheet>

                <v-sheet outlined class="pa-4 rounded-lg bg-lightgrey border-dashed d-flex align-center">
                  <v-checkbox v-model="user.is_staff" hide-details class="mt-0 pt-0"></v-checkbox>
                  <div>
                    <div class="body-2 font-weight-black primary--text">Acceso Staff</div>
                    <div class="caption grey--text">Permisos administrativos especiales</div>
                  </div>
                </v-sheet>

                <div v-if="user.is_seller" class="mt-4">
                  <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Comisión (%)</div>
                  <ValidationProvider name="Comision" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model='user.comision'
                      type="number"
                      outlined dense
                      suffix="%"
                      :error-messages="errors"
                      class="modern-input"
                    ></v-text-field>
                  </ValidationProvider>
                </div>

                <div class="mt-2">
                  <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Unidad de Negocios</div>
                  <v-text-field
                    v-model='user.bussiness_unit'
                    outlined dense
                    placeholder="Ej: Manufactura"
                    class="modern-input"
                  ></v-text-field>
                </div>

                <div v-if="hasSoftnetIntegration" class="mt-2">
                  <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">SOFTNET ID</div>
                  <v-text-field
                    v-model='user.softnet_id'
                    outlined dense
                    class="modern-input"
                  ></v-text-field>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-form>
    </ValidationObserver>
  </v-container>
</template>

<script>
import { getUser, createUser, updateUser, passwordResetSendEmail, fetchRoles } from '@/api/auth'
import notifications from '@/utils/notifications'

export default {
  name: 'CreateupdateUser',
  data: () => ({
    user: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      password: '',
      is_seller: false,
      is_staff: false,
      comision: 0,
      role: '2',
      bussiness_unit: '',
    },
    loading: false,
    sent: false,
    error: false,
    roles: []
  }),
  async created() {
    this.loading = true
    try {
      if (this.$route.params.id) {
        const user = await getUser(this.$route.params.id)
        this.user = user
      }
      this.roles = await fetchRoles()
    } catch (e) {
      notifications.showError('Error de conexión ' + e)
    } finally {
      this.loading = false
    }
  },
  methods: {
    async saveUser() {
      this.loading = true
      try {
        if (this.user.id) {
          await updateUser(this.user)
          notifications.showSuccess(`Usuario ${this.user.first_name} ${this.user.last_name} Actualizado`)
        } else {
          const res = await createUser({
            email: this.user.email,
            username: this.user.email,
            password: this.user.password,
          })
          this.user.id = res.id
          await updateUser(this.user)
          notifications.showSuccess(`Usuario ${this.user.first_name} ${this.user.last_name} Creado`)
        }
        await this.$store.dispatch('getSellers')
        this.$router.push({ name: 'Users' })
      } catch (e) {
        notifications.showError('Error al procesar el usuario: ' + e)
      } finally {
        this.loading = false
      }
    },
    async sendEmail() {
      this.loading = true
      try {
        await passwordResetSendEmail(this.user.email)
        this.sent = true
        this.error = false
        notifications.showSuccess('Correo de restauración enviado')
      } catch (e) {
        this.sent = true
        this.error = true
        notifications.showError('Error al enviar el correo')
      } finally {
        this.loading = false
      }
    },
  },
  computed: {
    hasSoftnetIntegration() {
      const company = this.$store.getters.getCompany
      return company && company.softnet_integration_enabled
    }
  }
}
</script>

<style scoped>
.modern-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.border-white-sm { border: 2px solid white !important; }

/* Input Styling */
.modern-input >>> .v-input__control >>> .v-input__slot {
  background-color: #f8fbfc !important;
  border-radius: 8px !important;
  border-width: 1px !important;
}

.bg-lightgrey { background-color: #f8fbfc !important; }
.border-dashed { border: 1px dashed #cfd8dc !important; }
.uppercase { text-transform: uppercase; letter-spacing: 1px; }
.tracking-widest { letter-spacing: 2px; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.25 !important; }
</style>